import React from 'react';
import { makeStyles, Grid, Card, CardContent, Typography, Box } from '@material-ui/core';
import {
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import StorageIcon from '@material-ui/icons/Storage';
import CodeIcon from '@material-ui/icons/Code';
import DescriptionIcon from '@material-ui/icons/Description';
import GroupIcon from '@material-ui/icons/Group';
import { dark, light } from '../../theme/tokens';

const useStyles = makeStyles(theme => {
  const t = theme.palette.type === 'dark' ? dark : light;
  return {
    statCard: {
      height: '100%',
      backgroundColor: t.surface,
    },
    statContent: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.75),
      padding: theme.spacing(2, 2.25),
      '&:last-child': { paddingBottom: theme.spacing(2) },
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 42,
      height: 42,
      borderRadius: 10,
      background: t.primary50,
      color: t.primary,
      flexShrink: 0,
    },
    icon: {
      fontSize: 20,
    },
    statInfo: {
      minWidth: 0,
    },
    statNumber: {
      fontSize: '1.375rem',
      fontWeight: 800,
      color: t.text,
      lineHeight: 1.1,
    },
    statLabel: {
      fontSize: '0.78rem',
      color: t.textMuted,
      marginTop: 2,
    },
    loadingText: {
      color: t.textMuted,
    },
  };
});

interface StatCardProps {
  icon: React.ReactNode;
  number: number | string;
  label: string;
  loading?: boolean;
}

const StatCard = ({ icon, number, label, loading }: StatCardProps) => {
  const classes = useStyles();

  return (
    <Card className={classes.statCard}>
      <CardContent className={classes.statContent}>
        <Box className={classes.iconWrapper}>
          {icon}
        </Box>
        <Box className={classes.statInfo}>
          {loading ? (
            <Typography className={classes.loadingText}>Loading...</Typography>
          ) : (
            <>
              <Typography className={classes.statNumber}>
                {number}
              </Typography>
              <Typography className={classes.statLabel}>
                {label}
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const StatsOverview = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const { value: stats, loading } = useAsync(async () => {
    try {
      const [services, apis, docs, groups] = await Promise.all([
        catalogApi.getEntities({ filter: { kind: 'Component' } }),
        catalogApi.getEntities({ filter: { kind: 'API' } }),
        catalogApi.getEntities({ filter: { kind: 'Component', 'spec.type': 'documentation' } }),
        catalogApi.getEntities({ filter: { kind: 'Group' } }),
      ]);

      return {
        services: services.items.length,
        apis: apis.items.length,
        docs: docs.items.length,
        groups: groups.items.length,
      };
    } catch (error) {
      // Return default values if there's an error
      return {
        services: 0,
        apis: 0,
        docs: 0,
        groups: 0,
      };
    }
  }, []);

  const statsData = [
    {
      icon: <StorageIcon className={classes.icon} />,
      number: stats?.services ?? 0,
      label: 'Services & Components',
    },
    {
      icon: <CodeIcon className={classes.icon} />,
      number: stats?.apis ?? 0,
      label: 'APIs',
    },
    {
      icon: <DescriptionIcon className={classes.icon} />,
      number: stats?.docs ?? 0,
      label: 'Documentation',
    },
    {
      icon: <GroupIcon className={classes.icon} />,
      number: stats?.groups ?? 0,
      label: 'Teams',
    },
  ];

  return (
    <Grid container spacing={3}>
      {statsData.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <StatCard {...stat} loading={loading} />
        </Grid>
      ))}
    </Grid>
  );
};

